import apiClient from './api';

/**
 * 会员服务类
 */
class MemberService {
  constructor() {
    this.memberInfo = null;
  }

  /**
   * 获取当前用户的会员信息
   */
  async getMemberInfo() {
    try {
      const response = await apiClient.get('/api/v1/member/info');
      this.memberInfo = response.data;
      return response.data;
    } catch (error) {
      console.error('获取会员信息失败:', error);
      throw new Error(`获取会员信息失败: ${error.response?.data?.detail || error.message}`);
    }
  }

  /**
   * 获取积分余额
   */
  async getBeanCoinsBalance() {
    try {
      const response = await apiClient.get('/api/v1/member/balance');
      return response.data;
    } catch (error) {
      console.error('获取积分余额失败:', error);
      throw new Error(`获取积分余额失败: ${error.response?.data?.detail || error.message}`);
    }
  }

  /**
   * 获取所有会员等级信息
   */
  async getMemberLevels() {
    try {
      const response = await apiClient.get('/api/v1/member/levels');
      return response.data;
    } catch (error) {
      console.error('获取会员等级失败:', error);
      throw new Error(`获取会员等级失败: ${error.response?.data?.detail || error.message}`);
    }
  }

  /**
   * 检查会员权限
   */
  async checkMemberPermissions(imagesCount) {
    try {
      const response = await apiClient.get('/api/v1/member/permissions', {
        params: { images_count: imagesCount }
      });
      return response.data;
    } catch (error) {
      console.error('检查会员权限失败:', error);
      throw new Error(`检查会员权限失败: ${error.response?.data?.detail || error.message}`);
    }
  }

  // 用户自充值功能已移除，现在由管理员通过脚本进行人工充值
  // 管理员可以使用：python scripts/admin_recharge.py add-coins <邮箱> <积分数量>

  /**
   * 升级为会员
   */
  async upgradeToMember(initialCoins = 0) {
    try {
      const response = await apiClient.post('/api/v1/member/upgrade', {
        amount: initialCoins
      });
      
      // 更新本地缓存的会员信息
      if (this.memberInfo) {
        this.memberInfo.is_member = true;
        this.memberInfo.bean_coins = response.data.current_bean_coins;
      }
      
      return response.data;
    } catch (error) {
      console.error('升级会员失败:', error);
      throw new Error(`升级会员失败: ${error.response?.data?.detail || error.message}`);
    }
  }

  /**
   * 刷新会员信息
   */
  async refreshMemberInfo() {
    try {
      const memberInfo = await this.getMemberInfo();
      
      // 触发会员信息更新事件
      window.dispatchEvent(new CustomEvent('member-info-updated', { 
        detail: { memberInfo } 
      }));
      
      return memberInfo;
    } catch (error) {
      console.error('刷新会员信息失败:', error);
      throw error;
    }
  }

  /**
   * 获取缓存的会员信息
   */
  getCachedMemberInfo() {
    return this.memberInfo;
  }

  /**
   * 清除缓存的会员信息
   */
  clearMemberInfo() {
    this.memberInfo = null;
  }

  /**
   * 检查是否为会员
   */
  isMember() {
    return this.memberInfo?.is_member || false;
  }

  /**
   * 获取积分数量
   */
  getBeanCoins() {
    return this.memberInfo?.bean_coins || 0;
  }

  /**
   * 获取最大可处理图片数量
   */
  getMaxImages() {
    return this.memberInfo?.max_images || 0;
  }

  /**
   * 获取会员等级
   */
  getMemberLevel() {
    return this.memberInfo?.member_level || 'normal';
  }

  /**
   * 获取会员等级名称
   */
  getMemberLevelName() {
    return this.memberInfo?.member_level_name || '普通用户';
  }

  /**
   * 获取累计充值金额
   */
  getTotalRechargeAmount() {
    return this.memberInfo?.total_recharge_amount || 0;
  }

  /**
   * 获取当前折扣率
   */
  getCurrentDiscountRate() {
    return this.memberInfo?.current_discount_rate || 1.0;
  }

  /**
   * 获取下一等级信息
   */
  getNextLevelInfo() {
    return {
      nextLevel: this.memberInfo?.next_level,
      nextLevelName: this.memberInfo?.next_level_name,
      amountToNextLevel: this.memberInfo?.amount_to_next_level
    };
  }

  /**
   * 格式化会员状态文本
   */
  getMemberStatusText() {
    const levelName = this.getMemberLevelName();
    const beanCoins = this.getBeanCoins();
    
    if (this.isMember()) {
      return `${levelName} (${beanCoins}积分)`;
    } else {
      return `${levelName} (${beanCoins}积分，单次最多20张)`;
    }
  }

  /**
   * 获取会员等级颜色
   */
  getMemberLevelColor() {
    const level = this.getMemberLevel();
    const colorMap = {
      'normal': '#666666',
      'white': '#d9d9d9',
      'silver': '#c0c0c0',
      'gold': '#ffd700',
      'diamond': '#b9f2ff',
      'supreme': '#ff6b6b'
    };
    return colorMap[level] || '#666666';
  }

  /**
   * 获取会员等级图标
   */
  getMemberLevelIcon() {
    const level = this.getMemberLevel();
    const iconMap = {
      'normal': '👤',
      'white': '🤍',
      'silver': '🥈',
      'gold': '🥇',
      'diamond': '💎',
      'supreme': '👑'
    };
    return iconMap[level] || '👤';
  }

  /**
   * 检查是否可以上传指定数量的图片
   */
  canUploadImages(count) {
    const beanCoins = this.getBeanCoins();
    
    // 所有用户都需要检查积分是否足够
    if (count > beanCoins) {
      return false;
    }
    
    if (this.isMember()) {
      // 会员用户没有数量限制，只检查积分
      return true;
    } else {
      // 非会员用户单次上传最多20张
      return count <= 20;
    }
  }

  /**
   * 获取会员权限提示信息
   */
  getPermissionTip(currentCount, requestCount) {
    const totalNeeded = currentCount + requestCount;
    const beanCoins = this.getBeanCoins();
    
    // 所有用户都需要检查积分是否足够
    if (totalNeeded > beanCoins) {
      return {
        canUpload: false,
        message: `积分不足，当前${currentCount}张，新增${requestCount}张需要${totalNeeded}积分，您只有${beanCoins}积分`
      };
    }
    
    if (this.isMember()) {
      // 会员用户没有数量限制，只检查积分
      return {
        canUpload: true,
        message: `会员用户可以上传，将消耗${totalNeeded}积分`
      };
    } else {
      // 非会员用户单次上传最多20张
      if (requestCount <= 20) {
        return {
          canUpload: true,
          message: `非会员用户可以上传，当前${currentCount}张，新增${requestCount}张，总计${totalNeeded}张`
        };
      } else {
        return {
          canUpload: false,
          message: `非会员用户单次最多只能上传20张图片，您尝试上传${requestCount}张`
        };
      }
    }
  }
}

// 创建全局实例
const memberService = new MemberService();

export default memberService; 